import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useUpdateAddress from '@/api/mutations/addresses/useUpdateAddress';
import { mergeOptimisticAddressUpdate } from '@/api/mutations/addresses/mergeOptimisticAddressUpdate';
import complianceKeys from '@/api/queries/compliance/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  ProjectByIdQuery,
  UpdateAddressInput,
  UpdateAddressMutation,
} from '@/gql/graphql';

const useOptimisticUpdateProjectAddress = (
  projectId: string,
  options?: UseMutationOptions<
    UpdateAddressMutation,
    unknown,
    UpdateAddressInput
  >
) => {
  const queryClient = useQueryClient();

  return useUpdateAddress({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOverview = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            projectById: {
              ...old.projectById,
              addresses: {
                ...old.projectById.addresses,
                site: old.projectById.addresses.site.map((oldSiteAddress) =>
                  oldSiteAddress.id === variables.id
                    ? mergeOptimisticAddressUpdate(oldSiteAddress, variables)
                    : oldSiteAddress
                ),
              },
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousProjectOverview };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOverview
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: complianceKeys.formPrefill(projectId),
      });
      // The site address drives the EPC-derived heat loss estimate, which in
      // turn determines whether existing Estimates are out of date.
      void queryClient.invalidateQueries({
        queryKey: projectKeys.heatLossEstimate(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.estimates(projectId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticUpdateProjectAddress;
